import { useEffect, useRef, useState } from 'react';

interface UseScrollRevealOptions {
  /** Fraction of element visible to trigger (0-1). Default 0.1 */
  threshold?: number;
  /** Root margin (e.g. "-50px" to trigger a bit before in view). Default "0px 0px -40px 0px" */
  rootMargin?: string;
  /** Run only once (true) or every time (false). Default true */
  once?: boolean;
}

/**
 * Hook that returns a ref and whether the element is in view.
 * Use with CSS classes for scroll-triggered reveal animations.
 */
export function useScrollReveal(options: UseScrollRevealOptions = {}) {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -40px 0px',
    once = true,
  } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}
